import type { GrowthOverviewGranularity } from "../const";
import { GrowthOverviewMetric } from "../const";
import type {
    GrowthOverviewDomain,
    GrowthOverviewIndustry,
    GrowthOverviewResponse,
    GrowthOverviewSubIndustry,
} from "../growthOverviewApiTypes";
import type { IGrowthOverviewPeriodCell, IGrowthOverviewTableRow } from "../types";
import { GrowthOverviewLevel } from "../types";

import { buildGrowthOverviewRowId } from "./buildGrowthOverviewRowId";
import { isValidGrowthOverviewPeriod } from "./buildPeriods";
import { buildPeriodSeries } from "./buildPeriodSeries";
import { normalizeGrowthOverviewGranularity } from "./granularityUtils";

export const getSummaryForMetric = (
    row: Pick<IGrowthOverviewTableRow, "summaryVisits" | "summaryUnique">,
    metric: GrowthOverviewMetric,
): number | null => (metric === GrowthOverviewMetric.Unique ? row.summaryUnique : row.summaryVisits);

export const getPeriodsForMetric = (
    row: Pick<IGrowthOverviewTableRow, "visitsPeriods" | "uniquePeriods">,
    metric: GrowthOverviewMetric,
): IGrowthOverviewPeriodCell[] => (metric === GrowthOverviewMetric.Unique ? row.uniquePeriods : row.visitsPeriods);

export const collectUnionPeriodHeaders = (
    rows: IGrowthOverviewTableRow[],
    metric: GrowthOverviewMetric,
): IGrowthOverviewPeriodCell[] => {
    const periodByKey = new Map<string, IGrowthOverviewPeriodCell>();

    const collectPeriods = (periods: IGrowthOverviewPeriodCell[]) => {
        periods.forEach((period) => {
            if (!isValidGrowthOverviewPeriod(period) || periodByKey.has(period.key)) {
                return;
            }
            periodByKey.set(period.key, period);
        });
    };

    const collectFromRow = (row: IGrowthOverviewTableRow) => {
        collectPeriods(getPeriodsForMetric(row, metric));
        if (metric === GrowthOverviewMetric.Unique) {
            collectPeriods(getPeriodsForMetric(row, GrowthOverviewMetric.Visits));
        }
        row.children?.forEach(collectFromRow);
    };

    rows.forEach(collectFromRow);

    return [...periodByKey.values()].toSorted((left, right) => left.date.localeCompare(right.date));
};

const mapApiDomainToTableRow = (
    row: GrowthOverviewDomain,
    granularity: GrowthOverviewGranularity,
    category: string,
): IGrowthOverviewTableRow => ({
    id: buildGrowthOverviewRowId("domain", category, row.domain),
    rowType: "domain",
    category,
    displayName: row.displayName,
    domain: row.domain ?? undefined,
    favicon: row.favicon ?? undefined,
    summaryVisits: row.visitsSum ?? null,
    summaryUnique: row.uniqueUsersSum ?? null,
    visitsPeriods: buildPeriodSeries(row.visits, granularity),
    uniquePeriods: buildPeriodSeries(row.unique, granularity),
});

const mapApiDomainsToTableRows = (
    rows: GrowthOverviewDomain[] | undefined | null,
    granularity: GrowthOverviewGranularity,
    category: string,
): IGrowthOverviewTableRow[] => (rows ?? []).map((row) => mapApiDomainToTableRow(row, granularity, category));

const buildSummaryPeriods = (
    summaryVisits: GrowthOverviewSubIndustry["summaryVisits"],
    summaryUniqueUsers: GrowthOverviewSubIndustry["summaryUniqueUsers"],
    granularity: GrowthOverviewGranularity,
): Pick<IGrowthOverviewTableRow, "visitsPeriods" | "uniquePeriods"> => ({
    visitsPeriods: buildPeriodSeries(summaryVisits?.visits, granularity),
    uniquePeriods: buildPeriodSeries(summaryUniqueUsers?.uniqueUsers, granularity),
});

export const mapApiSubIndustryToTableRow = (
    subIndustry: GrowthOverviewSubIndustry,
    granularity: GrowthOverviewGranularity,
): IGrowthOverviewTableRow => {
    const { visitsPeriods, uniquePeriods } = buildSummaryPeriods(
        subIndustry.summaryVisits,
        subIndustry.summaryUniqueUsers,
        granularity,
    );

    return {
        id: buildGrowthOverviewRowId("sub-industry", subIndustry.category),
        rowType: "sub-industry",
        category: subIndustry.category,
        displayName: subIndustry.displayName,
        summaryVisits: subIndustry.summaryVisits?.sum ?? null,
        summaryUnique: subIndustry.summaryUniqueUsers?.sum ?? null,
        visitsPeriods,
        uniquePeriods,
        domainCount: subIndustry.domainCount ?? undefined,
        children: undefined,
        childrenLoaded: false,
    };
};

const mapSubIndustriesToTableRows = (
    subIndustries: GrowthOverviewSubIndustry[],
    granularity: GrowthOverviewGranularity,
): IGrowthOverviewTableRow[] =>
    subIndustries.map((subIndustry) => mapApiSubIndustryToTableRow(subIndustry, granularity));

export const mapApiIndustryToTableRow = (
    industry: GrowthOverviewIndustry,
    granularity: GrowthOverviewGranularity,
): IGrowthOverviewTableRow => {
    const { visitsPeriods, uniquePeriods } = buildSummaryPeriods(
        industry.summaryVisits,
        industry.summaryUniqueUsers,
        granularity,
    );

    return {
        id: buildGrowthOverviewRowId("industry", industry.category),
        rowType: "industry",
        category: industry.category,
        displayName: industry.displayName,
        summaryVisits: industry.summaryVisits?.sum ?? null,
        summaryUnique: industry.summaryUniqueUsers?.sum ?? null,
        visitsPeriods,
        uniquePeriods,
        domainCount: industry.domainCount ?? undefined,
        children: undefined,
        childrenLoaded: false,
    };
};

const mapIndustriesToTableRows = (
    industries: GrowthOverviewIndustry[],
    granularity: GrowthOverviewGranularity,
): IGrowthOverviewTableRow[] => industries.map((industry) => mapApiIndustryToTableRow(industry, granularity));

export const mapBranchResponseToTableRows = (response: GrowthOverviewResponse): IGrowthOverviewTableRow[] => {
    const granularity = normalizeGrowthOverviewGranularity(response.granularity);

    switch (response.level) {
        case GrowthOverviewLevel.Industries:
            return mapIndustriesToTableRows(response.industries ?? [], granularity);
        case GrowthOverviewLevel.SubIndustries:
            return mapSubIndustriesToTableRows(response.subIndustries ?? [], granularity);
        case GrowthOverviewLevel.Domains:
            return mapApiDomainsToTableRows(response.domains ?? [], granularity, response.category);
        default:
            return [];
    }
};

export const mapApiResponseToTableRows = (response: GrowthOverviewResponse): IGrowthOverviewTableRow[] =>
    mapBranchResponseToTableRows(response);
